import React from "react";
import PermissionContext from "./PermissionContext";
import { UserType } from "node-library/dist/constants";

export enum Permission {
	ALL = "All",
	CLIENT_INFO = "Client Info",
	PRICE = "Price",
	ADMIN = "Admin",
	ENGINEER_ADMIN = "Engineer Admin",
	ENGINEER = "Engineer",
	SHIPPING = "Shipping",
	CLIENT = "Client",
}

export const rolePermissions: Record<UserType, Permission[]> = {
	[UserType.SYSTEM_ADMIN]: [Permission.CLIENT_INFO, Permission.PRICE],
	[UserType.ADMIN]: [
		Permission.CLIENT_INFO,
		Permission.PRICE,
		Permission.ADMIN,
		Permission.ENGINEER,
		Permission.SHIPPING,
		Permission.CLIENT,
	],
	[UserType.ENGINEER_ADMIN]: [Permission.ENGINEER_ADMIN, Permission.ENGINEER], // Engineers can't see price and client info
	[UserType.ENGINEER]: [Permission.ENGINEER], // Engineers can't see price and client info
	[UserType.SHIPPING]: [Permission.SHIPPING],
	[UserType.CLIENT]: [Permission.PRICE, Permission.CLIENT],
};

interface PermissionProviderProps {
	role: UserType;
	children: React.ReactNode;
}

// This provider is intended to be surrounding the whole application.
// It should receive the users permissions as parameter
const PermissionProvider: React.FunctionComponent<PermissionProviderProps> = ({
	role,
	children,
}) => {
	// Returns whether the requested permission is available for the role
	// passed permission as parameter
	const isAllowedTo = (permission: Permission) => {
		const isAllowed =
			(permission === Permission.ALL ||
				rolePermissions[role]?.includes(permission)) ??
			false;
		return isAllowed;
	};

	// This component will render its children wrapped around a PermissionContext's provider whose
	// value is set to the method defined above
	return (
		<PermissionContext.Provider value={{ isAllowedTo }}>
			{children}
		</PermissionContext.Provider>
	);
};

export default PermissionProvider;
